import { autoUpdate, computePosition, offset, shift, type VirtualElement } from "@floating-ui/dom";
import type { Editor } from "@tiptap/core";
import { posToDOMRect } from "@tiptap/react";
import { CheckIcon, Link2Icon, Trash2Icon, XIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useSkillCollectionEditorStore } from "../store";
import {
  buildAbsoluteSkillCollectionHref,
  formatSkillRelativePathForDisplay,
  getItemName,
  resolveSkillCollectionLinkPath,
} from "../utils/path-utils";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";

interface SkillCollectionInlineLinkMenuProps {
  editor: Editor;
}

interface EditorSelectionState {
  from: number;
  to: number;
  empty: boolean;
  href: string;
  isLinkActive: boolean;
}

interface StoredSelectionRange {
  from: number;
  to: number;
  isLinkActive: boolean;
}

interface FileLinkOption {
  id: string;
  displayPath: string;
  href: string;
}

const getEditorSelectionState = (editor: Editor): EditorSelectionState => {
  const { from, to, empty } = editor.state.selection;
  const { href } = editor.getAttributes("link");

  return {
    from,
    to,
    empty,
    href: typeof href === "string" ? href : "",
    isLinkActive: editor.isActive("link"),
  };
};

export const SkillCollectionInlineLinkMenu = ({ editor }: SkillCollectionInlineLinkMenuProps) => {
  const store = useSkillCollectionEditorStore();
  const [selectionState, setSelectionState] = useState(() => getEditorSelectionState(editor));
  const [isEditorFocused, setIsEditorFocused] = useState(editor.isFocused);
  const [isEditing, setIsEditing] = useState(false);
  const [linkHref, setLinkHref] = useState("");
  const menuRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const storedSelectionRef = useRef<StoredSelectionRange | null>(null);
  const currentFilePath = store.selectedPath ?? "";
  const availableFilePaths = useMemo(() => new Set(store.filePaths), [store.filePaths]);
  const availableFileOptions = useMemo(
    () =>
      [...store.filePaths]
        .filter(path => path !== currentFilePath && getItemName(path) !== ".keep.md")
        .sort((a, b) => a.localeCompare(b))
        .map<FileLinkOption>(path => ({
          id: path,
          displayPath: formatSkillRelativePathForDisplay(path),
          href: buildAbsoluteSkillCollectionHref(path),
        })),
    [currentFilePath, store.filePaths],
  );
  const filteredFileOptions = useMemo(() => {
    const normalizedQuery = linkHref.trim().toLowerCase();
    if (!normalizedQuery) {
      return availableFileOptions.slice(0, 8);
    }

    return availableFileOptions
      .filter(
        option =>
          option.id.toLowerCase().includes(normalizedQuery) ||
          option.displayPath.toLowerCase().includes(normalizedQuery) ||
          option.href.toLowerCase().includes(normalizedQuery),
      )
      .slice(0, 8);
  }, [availableFileOptions, linkHref]);

  const shouldShowMenu = useMemo(
    () => (isEditorFocused || isEditing) && (selectionState.isLinkActive || !selectionState.empty),
    [isEditing, isEditorFocused, selectionState.empty, selectionState.isLinkActive],
  );

  const getAnchorSelection = useCallback(
    () =>
      isEditing && storedSelectionRef.current
        ? storedSelectionRef.current
        : {
            from: selectionState.from,
            to: selectionState.to,
            isLinkActive: selectionState.isLinkActive,
          },
    [isEditing, selectionState.from, selectionState.isLinkActive, selectionState.to],
  );

  const updatePosition = useCallback(() => {
    if (!menuRef.current || !shouldShowMenu) {
      return;
    }

    const currentSelection = getAnchorSelection();

    const virtualElement: VirtualElement = {
      getBoundingClientRect: () =>
        posToDOMRect(editor.view, currentSelection.from, currentSelection.to),
      contextElement: editor.view.dom,
    };

    computePosition(virtualElement, menuRef.current, {
      placement: "top-start",
      strategy: "fixed",
      middleware: [offset(10), shift({ padding: 8 })],
    }).then(({ x, y }) => {
      if (!menuRef.current) {
        return;
      }

      menuRef.current.style.left = `${x}px`;
      menuRef.current.style.top = `${y}px`;
    });
  }, [editor.view, getAnchorSelection, shouldShowMenu]);

  useEffect(() => {
    const syncSelectionState = () => {
      setSelectionState(getEditorSelectionState(editor));
    };
    const handleFocus = () => {
      setIsEditorFocused(true);
      syncSelectionState();
    };
    const handleBlur = () => {
      setIsEditorFocused(false);
      if (!isEditing) {
        storedSelectionRef.current = null;
      }
    };

    syncSelectionState();
    editor.on("selectionUpdate", syncSelectionState);
    editor.on("transaction", syncSelectionState);
    editor.on("focus", handleFocus);
    editor.on("blur", handleBlur);

    return () => {
      editor.off("selectionUpdate", syncSelectionState);
      editor.off("transaction", syncSelectionState);
      editor.off("focus", handleFocus);
      editor.off("blur", handleBlur);
    };
  }, [editor, isEditing]);

  useEffect(() => {
    if (!shouldShowMenu) {
      setIsEditing(false);
      return;
    }

    const menuElement = menuRef.current;
    if (!menuElement) {
      return;
    }

    const virtualElement: VirtualElement = {
      getBoundingClientRect: () => {
        const currentSelection = getAnchorSelection();
        return posToDOMRect(editor.view, currentSelection.from, currentSelection.to);
      },
      contextElement: editor.view.dom,
    };

    updatePosition();

    return autoUpdate(virtualElement, menuElement, updatePosition);
  }, [editor.view, getAnchorSelection, shouldShowMenu, updatePosition]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
      updatePosition();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isEditing, updatePosition]);

  const beginEditing = useCallback(() => {
    storedSelectionRef.current = {
      from: selectionState.from,
      to: selectionState.to,
      isLinkActive: selectionState.isLinkActive,
    };
    setLinkHref(selectionState.href);
    setIsEditing(true);
  }, [selectionState.from, selectionState.href, selectionState.isLinkActive, selectionState.to]);

  const handleSave = useCallback(() => {
    const trimmedHref = linkHref.trim();
    const resolvedInternalPath = resolveSkillCollectionLinkPath(
      trimmedHref,
      window.location.origin,
    );
    const normalizedHref =
      resolvedInternalPath && availableFilePaths.has(resolvedInternalPath)
        ? buildAbsoluteSkillCollectionHref(resolvedInternalPath)
        : trimmedHref;
    const storedSelection = storedSelectionRef.current ?? {
      from: selectionState.from,
      to: selectionState.to,
      isLinkActive: selectionState.isLinkActive,
    };
    const chain = editor.chain().focus().setTextSelection({
      from: storedSelection.from,
      to: storedSelection.to,
    });

    if (storedSelection.isLinkActive) {
      chain.extendMarkRange("link");
    }

    if (normalizedHref) {
      chain.setLink({ href: normalizedHref }).run();
    } else {
      chain.unsetLink().run();
    }

    setIsEditing(false);
    storedSelectionRef.current = null;
  }, [
    availableFilePaths,
    editor,
    linkHref,
    selectionState.from,
    selectionState.isLinkActive,
    selectionState.to,
  ]);

  const handleRemove = useCallback(() => {
    const storedSelection = storedSelectionRef.current ?? {
      from: selectionState.from,
      to: selectionState.to,
      isLinkActive: selectionState.isLinkActive,
    };

    editor
      .chain()
      .focus()
      .setTextSelection({
        from: storedSelection.from,
        to: storedSelection.to,
      })
      .extendMarkRange("link")
      .unsetLink()
      .run();

    setIsEditing(false);
    storedSelectionRef.current = null;
  }, [editor, selectionState.from, selectionState.isLinkActive, selectionState.to]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    storedSelectionRef.current = null;
    editor.commands.focus();
  }, [editor.commands]);

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSave();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        handleCancel();
      }
    },
    [handleCancel, handleSave],
  );

  if (!shouldShowMenu) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[100] flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-2 py-1.5 shadow-lg"
    >
      {isEditing ? (
        <div className="flex min-w-[360px] max-w-[420px] flex-col gap-2">
          <div className="flex items-center gap-2">
            <Link2Icon className="h-4 w-4 shrink-0 text-gray-400" />
            <Input
              ref={inputRef}
              value={linkHref}
              onChange={event => setLinkHref(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Paste or type a link..."
              className="h-8 min-w-0 flex-1 border-0 px-2 py-1 text-sm shadow-none focus-visible:ring-0"
            />
            <Button
              type="button"
              size="tinyIcon"
              variant="ghost"
              onMouseDown={event => event.preventDefault()}
              onClick={handleSave}
              aria-label="Save link"
            >
              <CheckIcon className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="tinyIcon"
              variant="ghost"
              onMouseDown={event => event.preventDefault()}
              onClick={handleCancel}
              aria-label="Cancel link editing"
            >
              <XIcon className="h-4 w-4" />
            </Button>
            {selectionState.isLinkActive && (
              <Button
                type="button"
                size="tinyIcon"
                variant="ghost"
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                onMouseDown={event => event.preventDefault()}
                onClick={handleRemove}
                aria-label="Remove link"
              >
                <Trash2Icon className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-1 py-1">
            <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
              Available files
            </div>
            {filteredFileOptions.length > 0 ? (
              <div className="max-h-48 overflow-y-auto">
                {filteredFileOptions.map(option => (
                  <button
                    key={option.id}
                    type="button"
                    className="flex w-full flex-col rounded-md px-2 py-1.5 text-left hover:bg-white"
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => setLinkHref(option.href)}
                  >
                    <OverflowDisplayTooltip content={option.displayPath}>
                      {option.displayPath}
                    </OverflowDisplayTooltip>
                    <span className="truncate text-xs text-gray-500">{option.href}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-2 py-1 text-xs text-gray-500">No matching files.</div>
            )}
          </div>
        </div>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-2 px-2"
          onMouseDown={event => event.preventDefault()}
          onClick={beginEditing}
        >
          <Link2Icon className="h-4 w-4" />
          {selectionState.isLinkActive ? "Edit link" : "Add link"}
        </Button>
      )}
    </div>,
    document.body,
  );
};
